import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, RefreshCw, X, Twitter } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

function CopyButton({ value, label = "Copy", testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("✓");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard error");
    }
  };
  return (
    <button
      data-testid={testId}
      onClick={onCopy}
      className="inline-flex items-center gap-2 px-3 py-2 border border-current font-mono-tech text-[10px] uppercase tracking-[0.25em] hover:bg-current hover:text-white transition-colors"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {label}
    </button>
  );
}

export default function PressKit({ kit, onClose, onRefresh, refreshing }) {
  const { lang } = useLang();
  if (!kit) return null;
  const fullText = [
    `🔬 GalascioDAS™ · ${lang === "es" ? "Resumen de prensa" : "Press summary"}`,
    "",
    ...kit.summary.map((s, i) => `${i + 1}. ${s}`),
    "",
    lang === "es" ? "CITAS EXTRAÍDAS:" : "EXTRACTED QUOTES:",
    ...kit.quotes.map((q) => `"${q}"`),
  ].join("\n");

  const tweetWithTags = `${kit.tweet}${kit.hashtags?.length ? "\n\n" + kit.hashtags.join(" ") : ""}`;
  const xIntent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetWithTags)}`;

  return (
    <div
      data-testid="press-kit-overlay"
      className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
    >
      <div
        data-testid="press-kit"
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-[#0A0A0A] max-w-[900px] w-full max-h-[92vh] overflow-y-auto subtle-scroll"
      >
        {/* Header */}
        <div className="bg-[#0A0A0A] text-white p-6 md:p-8 flex items-start justify-between gap-4 border-b-4 border-[#D90429]">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-2">
              · MODO · PRENSA · GALASCIODAS
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold leading-none">
              {lang === "es" ? "Kit de prensa" : "Press kit"}
            </h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="press-kit-refresh"
              onClick={onRefresh}
              disabled={refreshing}
              className="p-2 border border-white hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-40"
              aria-label="refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid="press-kit-close"
              onClick={onClose}
              className="p-2 border border-white hover:bg-[#D90429] hover:border-[#D90429] transition-colors"
              aria-label="close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-6 md:p-10 space-y-12">
          {/* Summary */}
          <section data-testid="press-summary">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
              <h3 className="font-serif-display text-2xl md:text-3xl font-bold uppercase">
                {lang === "es" ? "Resumen ejecutivo" : "Executive summary"}
              </h3>
              <CopyButton
                testId="copy-summary"
                value={kit.summary.map((s, i) => `${i + 1}. ${s}`).join("\n")}
                label={lang === "es" ? "Copiar resumen" : "Copy summary"}
              />
            </div>
            <ol className="space-y-3 border-t border-[#171717]">
              {kit.summary.map((s, i) => (
                <li
                  key={`s-${i}`}
                  className="grid grid-cols-[40px_1fr] gap-4 border-b border-[#E5E5E5] py-3 items-baseline"
                >
                  <span className="font-mono-tech text-xs font-bold text-[#D90429]">
                    0{i + 1}
                  </span>
                  <span className="font-serif-display text-lg leading-snug text-[#0A0A0A]">
                    {s}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          {/* Tweet */}
          <section data-testid="press-tweet" className="bg-[#FAFAFA] border border-[#0A0A0A] p-6">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
              <h3 className="font-serif-display text-2xl md:text-3xl font-bold uppercase">
                {lang === "es" ? "Listo para X" : "Ready for X"}
              </h3>
              <span className="font-mono-tech text-[10px] tracking-widest text-[#A3A3A3] uppercase">
                {kit.tweet.length} / 280
              </span>
            </div>
            <p
              data-testid="tweet-text"
              className="font-sans-body text-lg leading-relaxed text-[#0A0A0A] mb-4 border-l-2 border-[#D90429] pl-4"
            >
              {kit.tweet}
            </p>
            {kit.hashtags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="tweet-hashtags">
                {kit.hashtags.map((h) => (
                  <span
                    key={h}
                    className="font-mono-tech text-xs uppercase tracking-widest bg-[#0A0A0A] text-white px-2 py-1"
                  >
                    {h}
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              <a
                data-testid="post-on-x"
                href={xIntent}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-3 font-mono-tech text-xs uppercase tracking-[0.25em] border border-[#0A0A0A] hover:bg-[#D90429] hover:border-[#D90429] transition-colors"
              >
                <Twitter className="w-4 h-4" />
                {lang === "es" ? "Publicar en X" : "Post on X"}
              </a>
              <CopyButton
                testId="copy-tweet"
                value={tweetWithTags}
                label={lang === "es" ? "Copiar tweet" : "Copy tweet"}
              />
            </div>
          </section>

          {/* Quotes */}
          <section data-testid="press-quotes">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
              <h3 className="font-serif-display text-2xl md:text-3xl font-bold uppercase">
                {lang === "es" ? "Citas extraídas" : "Extracted quotes"}
              </h3>
              <CopyButton
                testId="copy-all-quotes"
                value={kit.quotes.map((q) => `"${q}"`).join("\n\n")}
                label={lang === "es" ? "Copiar todas" : "Copy all"}
              />
            </div>
            <div className="space-y-4">
              {kit.quotes.map((q, i) => (
                <article
                  key={`q-${i}`}
                  data-testid={`quote-${i}`}
                  className="border border-[#171717] p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-4"
                >
                  <blockquote className="font-serif-display italic text-lg leading-snug text-[#0A0A0A] before:content-['“'] before:text-3xl before:text-[#D90429] before:mr-1 after:content-['”'] after:text-3xl after:text-[#D90429] after:ml-1">
                    {q}
                  </blockquote>
                  <div className="shrink-0">
                    <CopyButton
                      testId={`copy-quote-${i}`}
                      value={`"${q}"`}
                      label={lang === "es" ? "Copiar" : "Copy"}
                    />
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* Full text copy */}
          <section className="border-t border-[#171717] pt-6 flex flex-wrap items-center justify-between gap-4">
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
              · KIT COMPLETO · TXT
            </div>
            <CopyButton
              testId="copy-full-kit"
              value={fullText}
              label={lang === "es" ? "Copiar kit completo" : "Copy full kit"}
            />
          </section>
        </div>
      </div>
    </div>
  );
}
